/**
 * Mobile Mega Menu Enhancement
 * Handles infinite scroll, lazy loading, and progressive disclosure
 */
class MobileMegaMenu {
  constructor() {
    this.loadedItems = new Map(); // Track loaded items per category
    this.isLoading = new Map(); // Prevent duplicate loading
    this.observedElements = new Set(); // Track intersection observers
    this.itemsPerLoad = 10; // Items to load per batch
    this.memoryThreshold = 100; // Max items before cleanup
    
    this.init();
  }

  init() {
    // Initialize on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.setupIntersectionObserver();
    this.setupLoadMoreButtons();
    this.initializeLoadedCounts();
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      console.warn('IntersectionObserver not supported, falling back to manual loading');
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    // Observe scroll triggers
    document.querySelectorAll('.header-2__mobile-mega-scroll-trigger').forEach(trigger => {
      this.observer.observe(trigger);
      this.observedElements.add(trigger);
    });
  }

  setupLoadMoreButtons() {
    document.querySelectorAll('.header-2__mobile-mega-load-more').forEach(button => {
      button.addEventListener('click', (e) => this.handleLoadMore(e));
    });
  }

  initializeLoadedCounts() {
    // Initialize counters for each category - start with 5 since we show 5 initially
    document.querySelectorAll('[data-mobile-mega-content]').forEach(container => {
      const category = container.dataset.category;
      if (category) {
        this.loadedItems.set(category, 5);
        this.isLoading.set(category, false);
        
        // Check if we have more data available
        const dataScript = document.querySelector(`[data-${category}-collections]`);
        if (dataScript) {
          try {
            const collectionsData = JSON.parse(dataScript.textContent);
            const hasMoreData = collectionsData && collectionsData !== "";
            
            // Show/hide the load more button based on available data
            const loadMoreButton = container.querySelector('.header-2__mobile-mega-load-more');
            if (loadMoreButton) {
              loadMoreButton.style.display = hasMoreData ? 'flex' : 'none';
            }
          } catch (error) {
            console.error(`Error checking data for ${category}:`, error);
          }
        }
      }
    });
  }

  async handleIntersection(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        const trigger = entry.target;
        const container = trigger.closest('[data-mobile-mega-content]');
        
        if (container) {
          const category = container.dataset.category;
          await this.loadMoreItems(category, container);
        }
      }
    }
  }

  async handleLoadMore(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const container = button.closest('[data-mobile-mega-content]');
    
    if (container) {
      const category = container.dataset.category;
      await this.loadMoreItems(category, container);
    }
  }

  async loadMoreItems(category, container) {
    if (this.isLoading.get(category)) {
      return; // Already loading
    }

    this.isLoading.set(category, true);
    this.showLoading(container);

    try {
      // Small delay for better UX
      await this.delay(300);
      
      const newItems = await this.fetchMoreItems(category);
      
      if (newItems && newItems.length > 0) {
        this.appendItems(container, newItems);
        this.updateLoadedCount(category, newItems.length);
        this.performMemoryCleanup(container);
        
        // Check if there are more items to load
        const dataScript = document.querySelector(`[data-${category}-collections]`);
        if (dataScript) {
          try {
            const collectionsData = JSON.parse(dataScript.textContent);
            const collections = collectionsData.split('~~');
            const loadedCount = this.loadedItems.get(category) || 5;
            const remainingItems = collections.length - (loadedCount - 5);
            
            if (remainingItems <= 0) {
              this.hideLoadMoreButton(container);
            }
          } catch (error) {
            console.error('Error checking remaining items:', error);
          }
        }
      } else {
        this.hideLoadMoreButton(container);
      }
    } catch (error) {
      console.error('Failed to load more items:', error);
      this.showError(container);
    } finally {
      this.hideLoading(container);
      this.isLoading.set(category, false);
    }
  }

  async fetchMoreItems(category) {
    // Get the collection data from the hidden JSON script tag
    const dataScript = document.querySelector(`[data-${category}-collections]`);
    if (!dataScript) {
      console.warn(`No data found for category: ${category}`);
      return [];
    }

    try {
      const collectionsData = JSON.parse(dataScript.textContent);
      if (!collectionsData || collectionsData === "") {
        console.log(`No more items to load for category: ${category}`);
        return [];
      }

      // Parse the collection data string
      const collections = collectionsData.split('~~');
      const loadedCount = this.loadedItems.get(category) || 5;
      const alreadyLoadedFromHidden = Math.max(0, loadedCount - 5);
      const startIndex = alreadyLoadedFromHidden;
      const endIndex = startIndex + this.itemsPerLoad;
      
      const itemsToLoad = collections.slice(startIndex, endIndex).map(collectionString => {
        const [url, title, id] = collectionString.split('|');
        return {
          url: url,
          title: title,
          id: id,
          html: `<a href="${url}" class="header-2__mobile-mega-link" data-collection-id="${id}">${title}</a>`
        };
      });

      return itemsToLoad;
    } catch (error) {
      console.error('Error parsing collection data:', error);
      return [];
    }
  }

  appendItems(container, items) {
    const lazyContainer = container.querySelector('[data-lazy-content]');
    if (!lazyContainer) return;

    items.forEach((item, index) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = item.html;
      const linkElement = tempDiv.firstElementChild;
      
      if (linkElement) {
        // Add to DOM first
        lazyContainer.appendChild(linkElement);
        
        // Add smooth fade-in animation with staggered timing
        setTimeout(() => {
          linkElement.classList.add('header-2__mobile-mega-link--fade-in');
        }, index * 100);
      }
    });
  }

  updateLoadedCount(category, newItemsCount) {
    const currentCount = this.loadedItems.get(category) || 0;
    this.loadedItems.set(category, currentCount + newItemsCount);
  }

  performMemoryCleanup(container) {
    const links = container.querySelectorAll('.header-2__mobile-mega-link');
    
    if (links.length > this.memoryThreshold) {
      // Hide items that are far from the current viewport
      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      
      links.forEach((link, index) => {
        const linkTop = link.offsetTop;
        const linkBottom = linkTop + link.offsetHeight;
        
        // Hide items that are more than 2 viewport heights away
        if (linkBottom < scrollTop - (containerHeight * 2) || 
            linkTop > scrollTop + (containerHeight * 3)) {
          link.classList.add('header-2__mobile-mega-link--hidden');
        } else {
          link.classList.remove('header-2__mobile-mega-link--hidden');
        }
      });
    }
  }

  showLoading(container) {
    const loadingElement = container.querySelector('.header-2__mobile-mega-loading');
    const loadMoreButton = container.querySelector('.header-2__mobile-mega-load-more');
    
    if (loadingElement) {
      loadingElement.style.display = 'flex';
    }
    if (loadMoreButton) {
      loadMoreButton.style.display = 'none';
    }
  }

  hideLoading(container) {
    const loadingElement = container.querySelector('.header-2__mobile-mega-loading');
    const loadMoreButton = container.querySelector('.header-2__mobile-mega-load-more');
    
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }
    if (loadMoreButton) {
      loadMoreButton.style.display = 'flex';
    }
  }

  hideLoadMoreButton(container) {
    const loadMoreButton = container.querySelector('.header-2__mobile-mega-load-more');
    if (loadMoreButton) {
      loadMoreButton.style.display = 'none';
    }
  }

  showError(container) {
    const loadingElement = container.querySelector('.header-2__mobile-mega-loading');
    if (loadingElement) {
      loadingElement.innerHTML = `
        <div style="color: #dc2626; text-align: center;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style="margin-bottom: 0.5rem;">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <div>Failed to load more items</div>
          <button onclick="location.reload()" style="margin-top: 1rem; padding: 0.5rem 1rem; background: #dc2626; color: white; border: none; border-radius: 0.25rem; cursor: pointer;">
            Retry
          </button>
        </div>
      `;
    }
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Cleanup method for when the mega menu is closed
  cleanup() {
    if (this.observer) {
      this.observedElements.forEach(element => {
        this.observer.unobserve(element);
      });
      this.observer.disconnect();
    }
    
    this.loadedItems.clear();
    this.isLoading.clear();
    this.observedElements.clear();
  }
}

// Initialize when the script loads
let mobileMegaMenuInstance;

function initMobileMegaMenu() {
  if (!mobileMegaMenuInstance) {
    mobileMegaMenuInstance = new MobileMegaMenu();
  }
}

function cleanupMobileMegaMenu() {
  if (mobileMegaMenuInstance) {
    mobileMegaMenuInstance.cleanup();
    mobileMegaMenuInstance = null;
  }
}

// Auto-initialize
initMobileMegaMenu();

// Export for manual control
window.MobileMegaMenu = {
  init: initMobileMegaMenu,
  cleanup: cleanupMobileMegaMenu,
  instance: () => mobileMegaMenuInstance
};